import React, { ComponentProps, JSXElementConstructor, ReactElement } from 'react';
import { default as Form } from '../../components/connectedForm';
import createForm from '../../components/connectedForm/ConnectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import { useMutation } from '@apollo/client';
import CustomerGlobalContact from '../../graphql/entities/customer/CustomerGlobalContact';
import { IDropdownProps } from '../../@types/dropdown';
import { EntityType } from '../../@types/entityTypes';
import ReferenceTableDropdown from '../../components/connectedForm/ReferenceTableDropdown';
import { CustomerContactAttributeConfig, JobTitle } from '../../graphql/entities';
import {
  BlurTextFieldType,
  HrefLink,
  IBlurTextFieldProps,
} from '../../components/connectedForm/BlurTextField/BlurTextField';
import { IAttributeArrayProps } from '../../components/connectedForm/AttributeArray';
import { ContactAddress, ContactMarketingSection, FullNameField } from './ContactTab';
import DocumentCheckboxTree from '../../components/connectedForm/DocumentCheckboxTree';
import { FilterOperation, ICustomerGlobalContact } from '../../graphql/schema/p4types';
import { IConnectedFormProps } from '../../@types/connectedFormTypes';
import CustomerWorkspace from './CustomerWorkspace';

/**
 * Note - Fields and layout here are based on ContactTab.tsx
 * There's a lot of complexity in there that isn't required here, which is why we're not using it directly.
 */
const CustomerGlobalContactForm = ({ values: customerGlobalContact }: { values: ICustomerGlobalContact }) => {
  const allowPhoneCalls = customerGlobalContact.allowPhoneCalls;
  const allowFax = customerGlobalContact.allowFax;

  return (
    <Form.FormContainer>
      <Form.Header entityHdr="name.fullName" component={Form.HeaderBlock} />
      <Form.Form>
        <Form.TabPanel>
          <Form.TabContent id="general" label="General">
            <Form.Row>
              <Form.Fieldset cols={1 / 2}>
                <Form.Legend label="Address Information" />
                <FullNameField dataid={''} />
                <Form.Field<IDropdownProps & { entity: EntityType; fieldName: string; saveParameter: string }>
                  dataid={'jobTitle'}
                  component={ReferenceTableDropdown}
                  label="Job Title"
                  entity={JobTitle}
                  fieldName="description"
                  saveParameter="jobTitle"
                  {...JobTitle.dropdown}
                />
                <ContactAddress dataid={''} />
                <Form.Field dataid={`deliveryType`} component={input.Dropdown} label="Delivery Type" />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 2}>
                <Form.Legend label="Contact Information" />
                <Form.Field<IBlurTextFieldProps>
                  dataid={`email`}
                  component={input.BlurTextField}
                  label="Email"
                  hyperlink={HrefLink.MailTo}
                  labelCols={4 / 12}
                  inputCols={8 / 12}
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid={`telephoneNumber`}
                  component={input.BlurTextField}
                  label="Telephone Number"
                  type={allowPhoneCalls ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
                  hint={allowPhoneCalls ? undefined : 'DO NOT CONTACT'}
                  hyperlink={HrefLink.Tel}
                  labelCols={4 / 12}
                  inputCols={8 / 12}
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid={`mobileNumber`}
                  component={input.BlurTextField}
                  label="Mobile Number"
                  type={allowPhoneCalls ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
                  hint={allowPhoneCalls ? undefined : 'DO NOT CONTACT'}
                  hyperlink={HrefLink.Tel}
                  labelCols={4 / 12}
                  inputCols={8 / 12}
                />
                <Form.Field<IBlurTextFieldProps>
                  dataid={`faxNumber`}
                  component={input.BlurTextField}
                  label="Fax Number"
                  type={allowFax ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
                  hint={allowFax ? undefined : 'DO NOT CONTACT'}
                  hyperlink={HrefLink.Tel}
                  labelCols={4 / 12}
                  inputCols={8 / 12}
                />
                <ContactMarketingSection dataid={''} type={'customer'} />
              </Form.Fieldset>
            </Form.Row>
            <Form.Legend label="Attributes" />
            <Form.Row>
              <Form.Fieldset cols={1}>
                <Form.Field<IAttributeArrayProps>
                  dataid={`attributeValues`}
                  component={input.AttributeArray}
                  label="Attributes"
                  query={CustomerContactAttributeConfig.list}
                  type={`customerContact`}
                />
              </Form.Fieldset>
            </Form.Row>
            <Form.Legend label="Additional Instruction Lines" />
            <Form.Row>
              <Form.Fieldset cols={1}>
                <Form.Field<IBlurTextFieldProps>
                  dataid={`additionalInstructionLines`}
                  component={input.BlurTextField}
                  includeLabel={false}
                  type={BlurTextFieldType.TextArea}
                  height={'55px'}
                />
              </Form.Fieldset>
            </Form.Row>
            <Form.Legend label="Documents" />
            <Form.Row>
              <DocumentCheckboxTree contactid={``} type={'customer'} />
            </Form.Row>
          </Form.TabContent>
          <Form.TabContent
            id="customers"
            label="Customers"
            shouldRender={!!customerGlobalContact?.id}
            tooltip={'View all Customers with a contact linked to this global contact record'}
          >
            <CustomerWorkspace
              embeddedView={true}
              viewScreenName={'customers/globalContacts/'}
              predefinedFilters={[
                {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: 'contacts.customerGlobalContact.id',
                    value: customerGlobalContact?.id,
                  },
                },
              ]}
            />
          </Form.TabContent>
        </Form.TabPanel>
      </Form.Form>
    </Form.FormContainer>
  );
};

const ConnectedCustomerGlobalContactForm = createForm({
  form: CustomerGlobalContact.type,
  name: CustomerGlobalContact.formTitle,
  entity: CustomerGlobalContact,
  queryFragment: CustomerGlobalContact.fragment,
  defaultReturnUrl: CustomerGlobalContact.workspaceUrl,
})(CustomerGlobalContactForm) as (
  props: Omit<ComponentProps<typeof CustomerGlobalContactForm>, 'values'> & IConnectedFormProps
) => ReactElement;

//To be moved out to it's own file if/when re-used.
type IConnectedFormWrapperProps<T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>> = Omit<
  ComponentProps<T>,
  'values' | 'readOnly' | 'formData' | 'formUpdate'
> & {
  readOnly?: boolean;
  overrideParams?: { id: number }; //Override location parameters.
  defaultVariables?: any; //Additional variables that will be passed to the form default query.
  additionalVariables?: any; //Additional variables that will be passed to the form instance query.
};

export default ({
  overrideParams,
  defaultVariables,
  ...props
}: IConnectedFormWrapperProps<typeof ConnectedCustomerGlobalContactForm>) => {
  const formDefault = useFormQuery(CustomerGlobalContact, {
    overrideParams: overrideParams,
    defaultVariables: defaultVariables,
  });
  const [formUpdate] = useMutation(CustomerGlobalContact.update!);
  const [formTemporaryUpdate] = useMutation(CustomerGlobalContact.temporarySave!);

  return (
    <ConnectedCustomerGlobalContactForm
      formTemporaryUpdate={formTemporaryUpdate}
      {...props}
      formUpdate={formUpdate}
      formData={formDefault}
    />
  );
};
